import React, { useEffect, useRef, useState } from 'react';

import { Button, CircularProgress, Dialog, DialogActions, DialogContent } from '@mui/material';
import ReactMarkdown from 'react-markdown';

import DialogCloseButton from '@components/DialogCloseButton';
import PlutoDialogTitle from '@components/PlutoDialogTitle';

import useCopyToClipboard from '@hooks/useCopyToClipboard';

import { PlutoIntelligenceIcon } from '@icons/custom/PlutoIntelligenceIcon';

interface AICellLineModalProps {
    open: boolean;
    onClose: () => void;
    cellLineName: string;
}

const AICellLineModal: React.FC<AICellLineModalProps> = ({ open, onClose, cellLineName }) => {
    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const { copyText, showCopySuccess } = useCopyToClipboard({ ref: contentRef });

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        setSummary(null);
        setError(null);

        fetch('/api/vertex/vertex-gemini', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                prompt: `Provide a detailed, markdown-formatted summary of the following cell line: ${cellLineName}. Format the response as a scientific researcher would want to view the information.  If there are relevant links to key references please include them.  Do not include any conversational intro or outro.`,
                model: 'gemini-2.0-flash',
            }),
        })
            .then(async (res) => {
                if (!res.ok) {
                    throw new Error('Failed to fetch AI summary.');
                }
                const data = await res.json();
                // The response can be double-wrapped, so we need to get to the core array
                let coreResponse = data.response;
                if (coreResponse.response && Array.isArray(coreResponse.response)) {
                    coreResponse = coreResponse.response;
                }

                const resp0 = Array.isArray(coreResponse) ? coreResponse[0] : coreResponse;
                let summaryText = resp0.text || 'No summary available.';

                // Remove conversational intro and outro
                const markdownStart = summaryText.indexOf('```markdown');
                const markdownEnd = summaryText.lastIndexOf('```');
                if (markdownStart !== -1 && markdownEnd > markdownStart) {
                    summaryText = summaryText.substring(markdownStart + 11, markdownEnd);
                }

                setSummary(summaryText);
            })
            .catch((err) => {
                setError(err.message || 'Unknown error');
            })
            .finally(() => setLoading(false));
    }, [open, cellLineName]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogCloseButton onClose={onClose} />
            <PlutoDialogTitle
                title="Pluto AI Cell Line Summary"
                iconStart={<PlutoIntelligenceIcon height={28} width={28} />}
            />
            <DialogContent ref={contentRef}>
                <div className="flex flex-col items-start mb-4">
                    <span className="text-sm text-gray-500">
                        Use PlutoIntelligence to summarize this cell line.
                        <br />
                        Disclaimer: AI-generated predictions are preliminary and should be independently verified.
                        <br />
                        Model: Gemini 2.0 Flash
                    </span>
                </div>
                <p className="text-lg font-semibold mb-2">{cellLineName}</p>
                {loading && (
                    <div className="flex items-center gap-2 mt-2">
                        <CircularProgress size={20} />
                        <p className="text-sm">Loading AI summary...</p>
                    </div>
                )}
                {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
                {summary && (
                    <div className="text-sm mt-2 prose max-w-full">
                        <ReactMarkdown>{summary}</ReactMarkdown>
                    </div>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => copyText()}>{showCopySuccess ? 'Copied!' : 'Copy to clipboard'}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default AICellLineModal;
